/**
 * Composable for tracking file open/download interactions on communications.
 *
 * When a communication requires a signature, each file must be opened before
 * the recipient can acknowledge. This composable tracks those interactions
 * and exposes an `allFilesViewed` computed for gating the ack form.
 *
 * No-op when `requiresSignature` is false.
 */

import { computed, onMounted, ref, type Ref } from 'vue'

import { apiFetch } from '@ja/portal-query'

import type {
  CommunicationAttachment,
  FileInteractionEvent,
  FileInteractionStatus,
} from '../types/communication'

export function useFileTracking(
  communicationId: Ref<string> | string,
  requiresSignature: Ref<boolean> | boolean,
  attachments: Ref<CommunicationAttachment[]>,
) {
  const interactionStatus = ref<FileInteractionStatus>({})

  // Internal timer state: tracks when each file was opened
  const openTimers = new Map<string, number>()

  // Debounce timers for page_viewed events
  const pageViewDebounce = new Map<string, ReturnType<typeof setTimeout>>()

  const resolveId = (): string =>
    typeof communicationId === 'string' ? communicationId : communicationId.value

  const isTracking = (): boolean => {
    const sig = typeof requiresSignature === 'boolean' ? requiresSignature : requiresSignature.value
    return sig && resolveId() !== ''
  }

  const allFilesViewed = computed(() => {
    if (!isTracking()) return true

    const files = attachments.value
    if (files.length === 0) return true

    for (const file of files) {
      const uuid = file.uuid
      if (!uuid) continue
      const status = interactionStatus.value[uuid]
      if (!status?.opened) return false
    }

    return true
  })

  function fireEvent(event: FileInteractionEvent): void {
    if (!isTracking()) return

    apiFetch(`/api/communications/${resolveId()}/file-interactions`, {
      method: 'POST',
      body: JSON.stringify(event),
    }).catch(() => {
      // Fire-and-forget: tracking failures must not block the user
    })
  }

  function trackOpen(attachment: CommunicationAttachment): void {
    if (!isTracking() || !attachment.uuid) return

    openTimers.set(attachment.uuid, performance.now())

    fireEvent({
      media_uuid: attachment.uuid,
      interaction_type: 'opened',
      file_name: attachment.name,
      mime_type: attachment.mime_type,
    })

    // Optimistically update local status
    const current = interactionStatus.value[attachment.uuid]
    interactionStatus.value = {
      ...interactionStatus.value,
      [attachment.uuid]: {
        opened: true,
        total_duration_seconds: current?.total_duration_seconds ?? 0,
        downloaded: current?.downloaded ?? false,
        open_count: (current?.open_count ?? 0) + 1,
      },
    }
  }

  function trackClose(attachment: CommunicationAttachment): void {
    if (!isTracking() || !attachment.uuid) return

    const startTime = openTimers.get(attachment.uuid)
    const durationSeconds = startTime
      ? Math.round((performance.now() - startTime) / 1000)
      : 0
    openTimers.delete(attachment.uuid)

    fireEvent({
      media_uuid: attachment.uuid,
      interaction_type: 'closed',
      duration_seconds: Math.min(durationSeconds, 86400),
    })

    // Update local duration
    const current = interactionStatus.value[attachment.uuid]
    if (current) {
      interactionStatus.value = {
        ...interactionStatus.value,
        [attachment.uuid]: {
          ...current,
          total_duration_seconds: current.total_duration_seconds + durationSeconds,
        },
      }
    }
  }

  function trackDownload(attachment: CommunicationAttachment): void {
    if (!isTracking() || !attachment.uuid) return

    fireEvent({
      media_uuid: attachment.uuid,
      interaction_type: 'downloaded',
      file_name: attachment.name,
      mime_type: attachment.mime_type,
    })

    // Optimistically update
    const current = interactionStatus.value[attachment.uuid]
    if (current) {
      interactionStatus.value = {
        ...interactionStatus.value,
        [attachment.uuid]: { ...current, downloaded: true },
      }
    }
  }

  function trackPageView(attachment: CommunicationAttachment, page: number): void {
    if (!isTracking() || !attachment.uuid) return

    // Debounce: only fire after 500ms of staying on the same page
    const key = `${attachment.uuid}:${page}`
    const existing = pageViewDebounce.get(key)
    if (existing) clearTimeout(existing)

    pageViewDebounce.set(key, setTimeout(() => {
      fireEvent({
        media_uuid: attachment.uuid!,
        interaction_type: 'page_viewed',
        page_number: page,
      })
      pageViewDebounce.delete(key)
    }, 500))
  }

  async function fetchStatus(): Promise<void> {
    if (!isTracking()) return

    try {
      const data = await apiFetch<FileInteractionStatus>(
        `/api/communications/${resolveId()}/file-interactions/status`,
      )
      if (data && typeof data === 'object') {
        interactionStatus.value = data
      }
    }
    catch {
      // Status fetch failure is non-critical
    }
  }

  onMounted(() => {
    if (isTracking()) {
      void fetchStatus()
    }
  })

  /**
   * Check whether a specific attachment has been opened at least once.
   */
  function isFileViewed(attachment: CommunicationAttachment): boolean {
    if (!isTracking() || !attachment.uuid) return true
    return interactionStatus.value[attachment.uuid]?.opened ?? false
  }

  return {
    interactionStatus,
    allFilesViewed,
    isFileViewed,
    trackOpen,
    trackClose,
    trackDownload,
    trackPageView,
    fetchStatus,
  }
}
